import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import PerfectScrollbar from 'react-perfect-scrollbar'
import PropTypes from 'prop-types'
import { Box, CircularProgress, Button, Chip } from '@material-ui/core'

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from 'components/Table'
import {
  BaseConfirmationDialog,
  ConfirmationDialog,
  LoadingFeedback,
  MenuButton,
  Permitted,
} from 'components'

import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'

import { routes } from 'Routes'
import helpers from 'helpers'
import permission from 'constants/permissions'
import constants from 'constants/index'
import useStyles from './styles'

const TableRowIncidents = ({ incident, setOpenComplete, refresh, ...rest }) => {
  const history = useHistory()
  const classes = useStyles()
  const snackbar = useSnackbar()

  const [openDialog, setOpenDialog] = useState(false)
  const [loading, setLoading] = useState(false)

  const { LABELS, LESSONS_LEARNED_ENUM } = constants.incidentManegement

  const stepLabel = () => {
    if (
      incident?.incidentManegementStep?.step === LESSONS_LEARNED_ENUM &&
      incident?.incidentManegementStep?.concluded
    ) {
      return 'Concluído'
    }

    return LABELS[incident?.incidentManegementStep?.step] || '-'
  }

  const navigateToShow = (incidentId) => {
    history.push(
      reverse(routes.incidents.show, {
        incidentId: incidentId,
      }),
    )
  }

  const navigateToEdit = (incidentId) => {
    history.push(
      reverse(routes.incidents.edit, {
        incidentId: incidentId,
      }),
    )
  }

  const handleDestroy = async () => {
    setLoading(true)
    try {
      handleActionDialog()
      await service.dponet.incidents.destroy({ incidentId: incident?.id })
      refresh()

      snackbar.open({
        message: 'Incidente excluído com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu um erro! Tente novamente.',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleActionDialog = () => {
    setOpenDialog((openDialog) => !openDialog)
  }

  const incidentTest =
    incident?.incidentType === constants.documents.TEST_INCIDENT_VALUE

  return (
    <>
      <LoadingFeedback open={loading} />
      <TableRow {...rest}>
        <TableCell>{incident?.id}</TableCell>
        <TableCell>{helpers.formatters.date(incident?.createdAt)}</TableCell>
        <TableCell>
          {helpers.formatters.date(incident?.incidentDatetime) || '-'}
        </TableCell>
        <TableCell>{incidentTest ? 'Teste' : 'Real'}</TableCell>
        <TableCell>
          <Chip
            label={incident && stepLabel()}
            className={helpers.nonComplianceReports.stepChipColor(
              incident?.incidentManegementStep?.step,
              incident?.incidentManegementStep?.concluded,
              classes,
            )}
            size="small"
          />
        </TableCell>
        <TableCell>
          {incident?.nonComplianceReport ? (
            <Chip
              label={helpers.nonComplianceReports
                .status(incident?.nonComplianceReport?.statusId)
                .toUpperCase()}
              className={helpers.nonComplianceReports.statusChipColor(
                incident?.nonComplianceReport?.statusId,
                classes,
              )}
              size="small"
            />
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell align="right">
          <MenuButton>
            <Button
              color="secondary"
              onClick={() => navigateToShow(incident?.id)}
              fullWidth
              size="small"
            >
              Visualizar
            </Button>
            <Permitted
              someTags={[
                permission.INCIDENTS.CREATE,
                permission.INCIDENTS.MANAGE,
              ]}
            >
              <Button
                color="secondary"
                onClick={() => navigateToEdit(incident?.id)}
                fullWidth
                size="small"
              >
                Editar
              </Button>
              {!incident?.legalOpinion && (
                <Button
                  color="secondary"
                  onClick={() => handleActionDialog()}
                  fullWidth
                  size="small"
                >
                  Excluir
                </Button>
              )}
            </Permitted>
          </MenuButton>
        </TableCell>
      </TableRow>
      <ConfirmationDialog
        open={openDialog}
        setOpen={setOpenDialog}
        text="Você tem certeza que deseja excluir o incidente?"
        textButtonCancel="Cancelar"
        textButton="Confirmar"
        actionAcceptButton={handleDestroy}
      />
    </>
  )
}

const IncidentsTable = ({ incidents, isLoading, refresh }) => {
  const [openComplete, setOpenComplete] = useState(false)

  const history = useHistory()

  const handleEditComplete = () => {
    if (openComplete === false) return

    history.push(
      reverse(routes.incidents.editComplete, {
        incidentId: openComplete,
      }),
    )
  }

  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          width="100%"
          minHeight="700px"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box width="100%">
          <PerfectScrollbar
            options={{ wheelPropagation: false, useBothWheelAxes: true }}
          >
            <Table
              size="small"
              emptyMessage="Nenhum incidente encontrado"
              isLoading={isLoading}
              noWrap
            >
              <TableHead>
                <TableRow>
                  <TableCell width="10%">Identificador</TableCell>
                  <TableCell width="20%">Data de criação</TableCell>
                  <TableCell width="20%">Data do incidente</TableCell>
                  <TableCell width="10%">Tipo</TableCell>
                  <TableCell width="20%">Fase</TableCell>
                  <TableCell width="20%">Status do Tratamento</TableCell>
                  <TableCell width="10%" align="right">
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidents?.map((incident) => (
                  <TableRowIncidents
                    incident={incident}
                    key={incident.id}
                    setOpenComplete={setOpenComplete}
                    refresh={refresh}
                    hover
                  />
                ))}
              </TableBody>
            </Table>
          </PerfectScrollbar>
        </Box>
      )}
      <BaseConfirmationDialog
        open={openComplete !== false}
        title="Você tem certeza de que deseja mudar para o formulário completo?"
        text="A equipe jurídica irá avaliar seu incidente e informá-lo sobre a necessidade de preencher o formulário completo. O modelo completo segue as diretrizes para notificar incidentes à ANPD, sendo abrangente e detalhado."
        actionButtonText="Confirmar"
        variant="warning"
        actionButton={handleEditComplete}
        setOpen={setOpenComplete}
        dialogSize="sm"
      />
    </>
  )
}

TableRowIncidents.propTypes = {
  incident: PropTypes.object,
}

export default IncidentsTable
